import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { MessageCircle, CheckCircle, Circle, Phone, Send, Copy } from 'lucide-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { EmojiPickerButton } from './EmojiPicker'
import { TemplatePicker } from './TemplatePicker'
import { LoadingSpinner, ConversationSkeleton, SendingMessage } from './LoadingSpinner'

interface Message {
  id: string
  phoneNumber: string
  timestamp: string
  message: string
  direction: 'inbound' | 'outbound'
  read: boolean
  twilioSid: string
  userId: string | null
  fromPhoneNumber?: string
  type?: 'message' | 'call_initiated' | 'call_answered' | 'call_completed' | 'call_failed' | 'voicemail'
  callSid?: string
  callStatus?: string
  callDuration?: string
  callType?: 'browser' | 'proxy'
  defaultDialedNumber?: string
  source?: 'api' | 'ui' | 'websocket'
  keywordMatches?: string[]
}

interface Conversation {
  conversationId: string
  userId: string
  userPhone: string
  contactPhone: string
  contactName: string | null
  contactEmail?: string | null
  lastMessageTimestamp: string
  lastMessagePreview: string
  hasUnread: boolean
  hasKeywordMatches: boolean
  lookupKey: string
}

interface ConversationsViewProps {
  apiUrl: string
  user: any
  userProfile: any
  selectedUserPhone: string | undefined
  userPhoneNumbers: Array<{phoneNumber: string, description: string | null}>
  onCallClick: (customerNumber: string) => void
  // WebSocket integration - messages will be added via websocket
  wsMessages: Message[]
  // Contact lookup
  contacts: { [phoneNumber: string]: { name: string; email?: string } }
  fetchContactIfNeeded: (phoneNumber: string) => void
  // Keyword state for highlighting
  userKeywords: Array<{id: string, text: string}>
  // Callback to update unread counts (number of conversations with unread messages per phone)
  onUnreadCountsChange?: (counts: { [phoneNumber: string]: number }) => void
  // Callback to extract contact names from conversations
  onConversationsLoaded?: (conversations: any[]) => void
}

export function ConversationsView({
  apiUrl,
  user,
  userProfile: _userProfile,
  selectedUserPhone,
  userPhoneNumbers: _userPhoneNumbers,
  onCallClick,
  wsMessages,
  contacts: _contacts,
  fetchContactIfNeeded,
  userKeywords: _userKeywords,
  onUnreadCountsChange,
  onConversationsLoaded
}: ConversationsViewProps) {
  // State
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [conversationMessages, setConversationMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [markingUnread, setMarkingUnread] = useState<string | null>(null)
  const [processedMessageIds, setProcessedMessageIds] = useState<Set<string>>(new Set())
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const replyInputRef = useRef<HTMLInputElement>(null)
  const conversationListRef = useRef<HTMLDivElement>(null)
  const lastSelectedConversationRef = useRef<string | null>(null)
  const autoMarkReadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitiallyFetchedRef = useRef(false)

  // Helper function to get display name (prioritize fetched contacts over conversation record)
  const getDisplayName = useCallback((contactPhone: string, fallbackName: string | null) => {
    // Check if we have fetched contact data
    if (_contacts[contactPhone]?.name) {
      return _contacts[contactPhone].name
    }
    // Fallback to conversation record name
    if (fallbackName) {
      return fallbackName
    }
    // Last resort: formatted phone number
    return formatPhoneNumber(contactPhone)
  }, [_contacts])

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!apiUrl || !user) return
    
    setLoading(true)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return
      
      console.log('📞 Fetching conversations from:', `${apiUrl}/app/conversations`)
      
      // Fetch all conversations (backend defaults to 1000, which should cover all users)
      const response = await fetch(`${apiUrl}/app/conversations`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Fetched conversations:', data.conversations?.length || 0, 'total')
        setConversations(data.conversations || [])
        
        // Extract contact names from conversations to avoid slow lookups
        if (onConversationsLoaded && data.conversations) {
          onConversationsLoaded(data.conversations)
        }
      } else {
        console.error('❌ Conversation fetch failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('📥 Error fetching conversations:', error)
    } finally {
      setLoading(false)
    }
  }, [apiUrl, user])

  // Fetch messages for a conversation
  const fetchConversationMessages = useCallback(async (conversationId: string) => {
    if (!apiUrl || !user) return
    
    setMessagesLoading(true)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return
      
      const response = await fetch(`${apiUrl}/app/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setConversationMessages(data.messages || [])
      }
    } catch (error) {
      console.error('📥 Error fetching conversation messages:', error)
    } finally {
      setMessagesLoading(false)
    }
  }, [apiUrl, user])

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (customerNumber: string) => {
    if (!apiUrl || !user) return

    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return

      const response = await fetch(`${apiUrl}/app/messages/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          customerNumber,
          fromPhoneNumber: selectedUserPhone
        })
      })

      if (response.ok) {
        // Update local state
        setConversationMessages(prev => 
          prev.map(msg => 
            msg.direction === 'inbound' ? { ...msg, read: true } : msg
          )
        )
        
        // Optimistic update: Clear unread and keyword flags immediately
        setConversations(prev => 
          prev.map(conv => 
            conv.contactPhone === customerNumber 
              ? { ...conv, hasUnread: false, hasKeywordMatches: false } 
              : conv
          )
        )
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error)
    }
  }, [apiUrl, user, selectedUserPhone])

  // Mark messages as unread
  const markMessagesAsUnread = useCallback(async (customerNumber: string) => {
    if (!apiUrl || !user) return

    setMarkingUnread(customerNumber)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return

      const response = await fetch(`${apiUrl}/app/messages/mark-unread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          customerNumber,
          fromPhoneNumber: selectedUserPhone
        })
      })

      if (response.ok) {
        // Refresh conversation list to get updated unread count
        fetchConversations()
      }
    } catch (error) {
      console.error('❌ Error marking as unread:', error)
    } finally {
      setMarkingUnread(null)
    }
  }, [apiUrl, user, selectedUserPhone, fetchConversations])

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user) return
    
    setSending(true)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return
      
      const response = await fetch(`${apiUrl}/app/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          to: selectedConversation.contactPhone,
          message: newMessage,
          fromPhoneNumber: selectedUserPhone,
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        
        // Add sent message to local state
        const newMsg: Message = {
          id: `outbound-${Date.now()}`,
          phoneNumber: selectedConversation.contactPhone,
          fromPhoneNumber: selectedUserPhone || '',
          timestamp: new Date().toISOString(),
          message: newMessage,
          direction: 'outbound',
          read: true,
          twilioSid: data.messageId || `SM${Math.random().toString(36).substr(2, 34)}`,
          userId: null,
          source: 'websocket'
        }
        
        setConversationMessages(prev => [...prev, newMsg])
        setNewMessage('')
        
        // Scroll to bottom
        setTimeout(() => scrollToBottom(), 100)
        
        // Refresh conversation list to update preview
        setTimeout(() => fetchConversations(), 500)
      }
    } catch (error) {
      console.error('❌ Error sending message:', error)
    } finally {
      setSending(false)
    }
  }

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Format phone number
  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4')
  }

  // Filter conversations based on search AND selected phone number
  const filteredConversations = useMemo(() => {
    let filtered = conversations
    
    // Filter by selected user phone number
    if (selectedUserPhone) {
      filtered = filtered.filter(conv => conv.userPhone === selectedUserPhone)
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(conv => {
        const phoneMatch = conv.contactPhone.toLowerCase().includes(searchQuery.toLowerCase())
        const nameMatch = conv.contactName?.toLowerCase().includes(searchQuery.toLowerCase())
        const fetchedNameMatch = _contacts[conv.contactPhone]?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        return phoneMatch || nameMatch || fetchedNameMatch
      })
    }
    
    return filtered
  }, [conversations, searchQuery, selectedUserPhone, _contacts])

  // Sort conversations by unread and timestamp
  const sortedConversations = useMemo(() => {
    return [...filteredConversations].sort((a, b) => {
      // Unread first
      if (a.hasUnread && !b.hasUnread) return -1
      if (!a.hasUnread && b.hasUnread) return 1
      
      // Then by timestamp
      return new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    })
  }, [filteredConversations])

  // Check if conversation has keyword matches (now from conversation record)
  const conversationHasKeywords = useCallback((conv: Conversation) => {
    return conv.hasKeywordMatches || false
  }, [])

  // Initial load
  useEffect(() => {
    if (apiUrl && user && !hasInitiallyFetchedRef.current) {
      hasInitiallyFetchedRef.current = true
      fetchConversations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, user])

  // Clear selected conversation if it's no longer in the filtered list (e.g., phone number changed)
  useEffect(() => {
    if (selectedConversation && selectedUserPhone) {
      if (selectedConversation.userPhone !== selectedUserPhone) {
        setSelectedConversation(null)
        setConversationMessages([])
      }
    }
  }, [selectedUserPhone, selectedConversation])

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!wsMessages || wsMessages.length === 0) return

    wsMessages.forEach((msg) => {
      // Skip if already processed
      const msgId = msg.twilioSid || `${msg.phoneNumber}-${msg.timestamp}`
      if (processedMessageIds.has(msgId)) return

      // Mark as processed
      setProcessedMessageIds(prev => new Set(prev).add(msgId))

      // Determine which conversation this message belongs to
      const isInbound = msg.direction === 'inbound'
      const contactPhone = isInbound ? msg.fromPhoneNumber : msg.phoneNumber
      const userPhone = isInbound ? msg.phoneNumber : msg.fromPhoneNumber

      // Update conversations list
      setConversations(prev => {
        const existingConvIndex = prev.findIndex(conv => 
          conv.contactPhone === contactPhone && conv.userPhone === userPhone
        )

        if (existingConvIndex >= 0) {
          // Update existing conversation
          const updated = [...prev]
          const conv = updated[existingConvIndex]
          
          // Check if message has keyword matches
          const hasKeywords = Array.isArray(msg.keywordMatches) && msg.keywordMatches.length > 0
          
          updated[existingConvIndex] = {
            ...conv,
            lastMessageTimestamp: msg.timestamp,
            lastMessagePreview: msg.message?.substring(0, 100) || '',
            hasUnread: isInbound ? true : conv.hasUnread,
            // Set hasKeywordMatches if this message has keywords, or keep existing value
            hasKeywordMatches: hasKeywords || conv.hasKeywordMatches
          }
          return updated
        } else {
          // New conversation (will be fetched on next refresh)
          return prev
        }
      })

      // If this message is for the currently selected conversation, add it to the messages
      if (selectedConversation && 
          selectedConversation.contactPhone === contactPhone &&
          selectedConversation.userPhone === userPhone) {
        setConversationMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => (m.twilioSid && m.twilioSid === msg.twilioSid))) return prev
          return [...prev, msg].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          )
        })
      }
    })
  }, [wsMessages, processedMessageIds, selectedConversation])

  // Auto-refresh conversations every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden && apiUrl && user) {
        fetchConversations()
      }
    }, 60000)
    
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, user])

  // Calculate and send unread conversation counts per phone to parent
  useEffect(() => {
    if (onUnreadCountsChange && conversations.length > 0) {
      const counts: { [phoneNumber: string]: number } = {}
      
      conversations.forEach(conv => {
        // Count conversations with any unread messages
        if (conv.hasUnread) {
          counts[conv.userPhone] = (counts[conv.userPhone] || 0) + 1
        }
      })
      
      onUnreadCountsChange(counts)
    }
  }, [conversations, onUnreadCountsChange])

  // Auto-scroll when messages change
  useEffect(() => {
    if (selectedConversation && conversationMessages.length > 0) {
      setTimeout(() => scrollToBottom(), 100)
    }
  }, [selectedConversation, conversationMessages.length])

  // Auto-mark as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && selectedConversation.conversationId !== lastSelectedConversationRef.current) {
      lastSelectedConversationRef.current = selectedConversation.conversationId
      
      // Clear any existing timeout
      if (autoMarkReadTimeoutRef.current) {
        clearTimeout(autoMarkReadTimeoutRef.current)
        autoMarkReadTimeoutRef.current = null
      }
      
      // Mark as read if there are unread messages
      if (selectedConversation.hasUnread) {
        markMessagesAsRead(selectedConversation.contactPhone)
        
        // Backup timeout
        autoMarkReadTimeoutRef.current = setTimeout(() => {
          markMessagesAsRead(selectedConversation.contactPhone)
        }, 2000)
      }
    }
    
    return () => {
      if (autoMarkReadTimeoutRef.current) {
        clearTimeout(autoMarkReadTimeoutRef.current)
        autoMarkReadTimeoutRef.current = null
      }
    }
  }, [selectedConversation, markMessagesAsRead])

  // Handle emoji select
  const handleEmojiSelect = (emoji: string) => {
    if (replyInputRef.current) {
      const start = replyInputRef.current.selectionStart ?? 0
      const end = replyInputRef.current.selectionEnd ?? 0
      const value = replyInputRef.current.value
      
      const newValue = value.substring(0, start) + emoji + value.substring(end)
      setNewMessage(newValue)
      
      const newCursorPos = start + emoji.length
      setTimeout(() => {
        replyInputRef.current!.setSelectionRange(newCursorPos, newCursorPos)
        replyInputRef.current!.focus()
      }, 0)
    }
  }

  // Handle template select
  const handleTemplateSelect = (content: string) => {
    setNewMessage(content)
    setTimeout(() => {
      replyInputRef.current?.focus()
    }, 0)
  }

  // Handle template create
  const handleTemplateCreate = async (name: string, content: string) => {
    if (!apiUrl || !user) return

    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()
      
      if (!jwtToken) return

      const response = await fetch(`${apiUrl}/app/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ name, content })
      })

      if (!response.ok) {
        throw new Error('Failed to create template')
      }
    } catch (error) {
      throw error
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 conversation-layout min-h-[80vh]">
      {/* Sidebar - Conversations */}
      <div className={`lg:col-span-1 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
          <div className="flex flex-col space-y-1.5 p-4 sm:p-6 border-b">
            <h3 className="text-lg font-semibold leading-none tracking-tight flex items-center space-x-2">
              <MessageCircle className="h-5 w-5" />
              <span>Conversations</span>
            </h3>
          </div>
          <div className="p-4 sm:p-6">
            {/* Search input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            
            <div ref={conversationListRef} className="space-y-1 max-h-64 sm:max-h-96 overflow-y-auto">
              {loading ? (
                <ConversationSkeleton />
              ) : (
                <>
                  {sortedConversations.map((conv) => {
                    const hasUnread = conv.hasUnread
                    const hasKeywords = conversationHasKeywords(conv)
                    
                    return (
                      <button
                        key={conv.conversationId}
                        className={`w-full text-left p-2 sm:p-3 rounded-md transition-colors group ${
                          selectedConversation?.conversationId === conv.conversationId
                            ? 'bg-accent text-accent-foreground' 
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                        onClick={(e) => {
                          e.preventDefault()
                          setSelectedConversation(conv)
                          fetchConversationMessages(conv.conversationId)
                          // Fetch contact info only if conversation doesn't have a name
                          if (!conv.contactName) {
                            fetchContactIfNeeded(conv.contactPhone)
                          }
                          setSidebarOpen(false)
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className="relative flex-shrink-0">
                              <div className={`w-3 h-3 rounded-full ${
                                hasUnread ? 'bg-red-500' : 'bg-green-500'
                              }`} />
                              {hasUnread && hasKeywords && (
                                <div className="absolute -inset-1 rounded-full border-2 border-red-400 animate-pulse" />
                              )}
                            </div>
                            <span className={`font-medium text-sm sm:text-base truncate ${
                              hasUnread ? 'font-semibold' : ''
                            }`}>
                              {getDisplayName(conv.contactPhone, conv.contactName)}
                            </span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                  {sortedConversations.length === 0 && (
                    <p className="text-muted-foreground text-sm text-center py-8">
                      {searchQuery.trim() ? 'No conversations found' : 'No conversations yet'}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Messages */}
      <div className="lg:col-span-3 flex flex-col h-[60vh] sm:h-[70vh]">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm flex flex-col h-full">
          <div className="flex flex-col space-y-1.5 p-4 sm:p-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold leading-none tracking-tight">
                  {selectedConversation 
                    ? getDisplayName(selectedConversation.contactPhone, selectedConversation.contactName)
                    : 'Select a conversation'}
                </h3>
                {selectedConversation && (
                  <div className="flex flex-col space-y-1 mt-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">
                        To: {formatPhoneNumber(selectedConversation.contactPhone)}
                      </span>
                      <div className="flex items-center space-x-0.5">
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(selectedConversation.contactPhone)
                            } catch (err) {
                              console.error('Copy failed:', err)
                            }
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1"
                          title="Copy phone number"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => onCallClick(selectedConversation.contactPhone)}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1"
                          title="Call this number"
                        >
                          <Phone className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {selectedConversation && (
                  <button
                    onClick={() => markMessagesAsUnread(selectedConversation.contactPhone)}
                    disabled={markingUnread === selectedConversation.contactPhone}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3 text-xs"
                    title="Mark conversation as unread"
                  >
                    {markingUnread === selectedConversation.contactPhone ? (
                      <LoadingSpinner type="marking" size="sm" />
                    ) : (
                      'Mark Unread'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {!selectedConversation ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageCircle className="h-16 w-16 mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Select a conversation</h2>
              <p className="text-sm">Choose a conversation from the sidebar to view messages and reply.</p>
            </div>
          ) : (
            <>
              <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
                <div className="space-y-3 sm:space-y-4">
                  {messagesLoading ? (
                    <div className="text-center py-8">
                      <LoadingSpinner type="conversations" />
                    </div>
                  ) : conversationMessages.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm sm:text-base text-muted-foreground">No messages yet</p>
                    </div>
                  ) : (
                    conversationMessages.map((message) => (
                      <div key={message.id} className="space-y-2 sm:space-y-3">
                        {/* Call Event Display */}
                        {message.type && message.type !== 'message' ? (
                          <div className="flex justify-center">
                            <div className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm w-[180px] ${
                              message.type === 'voicemail'
                                ? 'bg-orange-50 border border-orange-200 text-orange-700'
                                : message.callType === 'browser' 
                                ? 'bg-purple-50 border border-purple-200 text-purple-700' 
                                : 'bg-blue-50 border border-blue-200 text-blue-700'
                            }`}>
                              <div className="flex items-center space-x-2">
                                <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                                <span>
                                  {message.type === 'voicemail' && 'Voicemail received'}
                                  {message.type === 'call_initiated' && `${message.callType === 'browser' ? 'Call' : 'Relay call'} initiated`}
                                  {message.type === 'call_answered' && 'Call answered'}
                                  {message.type === 'call_completed' && `Call ended${message.callDuration ? ` (${message.callDuration}s)` : ''}`}
                                  {message.type === 'call_failed' && 'Call failed'}
                                </span>
                              </div>
                              <div className={`text-xs mt-1 ${
                                message.type === 'voicemail' 
                                  ? 'text-orange-600' 
                                  : message.callType === 'browser' 
                                  ? 'text-purple-600' 
                                  : 'text-blue-600'
                              }`}>
                                {formatTimestamp(message.timestamp)}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Regular Message Display */
                          <div className={`flex ${message.direction === 'inbound' ? 'justify-start' : 'justify-end'}`}>
                            <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 sm:py-3 rounded-2xl ${
                              message.direction === 'inbound' 
                                ? 'bg-muted border'
                                : message.source === 'api'
                                  ? 'bg-gray-100 text-gray-900 border border-gray-300'
                                  : 'bg-primary text-primary-foreground'
                            }`}>
                              <div className="flex items-start justify-between space-x-2">
                                <p className="text-xs sm:text-sm break-words">{message.message}</p>
                                <div className="flex items-center space-x-1 ml-2">
                                  {message.read ? (
                                    <CheckCircle className={`h-3 w-3 ${
                                      message.direction === 'inbound' 
                                        ? 'text-muted-foreground'
                                        : message.source === 'api'
                                          ? 'text-gray-600'
                                          : 'text-white'
                                    }`} />
                                  ) : (
                                    <Circle className={`h-3 w-3 ${
                                      message.direction === 'inbound' 
                                        ? 'text-muted-foreground'
                                        : message.source === 'api'
                                          ? 'text-gray-600'
                                          : 'text-white'
                                    }`} />
                                  )}
                                </div>
                              </div>
                              <div className={`text-xs mt-2 ${
                                message.direction === 'inbound' 
                                  ? 'text-muted-foreground' 
                                  : message.source === 'api'
                                    ? 'text-gray-600'
                                    : 'text-primary-foreground/70'
                              }`}>
                                {formatTimestamp(message.timestamp)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {sending && (
                    <div className="flex justify-end">
                      <SendingMessage />
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Reply Input */}
              <div className="border-t p-3 sm:p-4 bg-background" style={{ minHeight: '64px' }}>
                <div className="hidden sm:flex items-center space-x-2">
                  <input
                    ref={replyInputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        sendMessage()
                      }
                    }}
                  />
                  <EmojiPickerButton
                    onEmojiSelect={handleEmojiSelect}
                    disabled={!selectedConversation}
                  />
                  <TemplatePicker
                    onTemplateSelect={handleTemplateSelect}
                    onTemplateCreate={handleTemplateCreate}
                    disabled={!selectedConversation}
                    apiUrl={apiUrl}
                    user={user}
                  />
                  <button
                    onClick={() => onCallClick(selectedConversation.contactPhone)}
                    disabled={!selectedConversation}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3"
                    title="Call this number"
                  >
                    <Phone className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => sendMessage()}
                    disabled={sending || !newMessage.trim()}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
                  >
                    {sending ? (
                      <LoadingSpinner type="default" size="sm" hideText />
                    ) : (
                      <Send className="h-6 w-6" />
                    )}
                  </button>
                </div>

                {/* Mobile layout */}
                <div className="flex flex-col space-y-2 sm:hidden">
                  <div className="flex items-center space-x-2">
                    <input
                      ref={replyInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          sendMessage()
                        }
                      }}
                    />
                    <button
                      onClick={() => sendMessage()}
                      disabled={sending || !newMessage.trim()}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
                    >
                      {sending ? (
                        <LoadingSpinner type="default" size="sm" hideText />
                      ) : (
                        <Send className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <EmojiPickerButton
                        onEmojiSelect={handleEmojiSelect}
                        disabled={!selectedConversation}
                      />
                      <TemplatePicker
                        onTemplateSelect={handleTemplateSelect}
                        onTemplateCreate={handleTemplateCreate}
                        disabled={!selectedConversation}
                        apiUrl={apiUrl}
                        user={user}
                      />
                    </div>
                    <button
                      onClick={() => selectedConversation && onCallClick(selectedConversation.contactPhone)}
                      disabled={!selectedConversation}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3"
                      title="Call this number"
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile sidebar toggle - Hidden on desktop (≥1024px) */}
      {!selectedConversation && (
        <div className="lg:hidden fixed bottom-4 right-4 z-50">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg h-14 w-14 hover:bg-primary/90 transition-colors"
          >
            <MessageCircle className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  )
}

